// O formulário da distribuição no tempo de um nível da EAP na origem "criar do zero"
// (design D12): a competência de INÍCIO e a FORMA — parcelas mensais iguais ou uma tabela
// personalizada com o percentual de cada mês depois do início. O que se define vale para tudo
// abaixo do nível até um nível mais específico sobrescrever.
//
// A validação é a do `budgetPlanner` (a porta do planejador do servidor): a forma soma 100%,
// cada parcela é maior que zero e cabe nas 120 parcelas. O servidor valida de novo.
import { useState } from "react";

import {
  BudgetPlannerError,
  MAX_DISTRIBUTION_LINES,
  equalDistributionProfile,
  validateDistribution,
} from "./budgetPlanner";
import { percentInputToBps, bpsToPercentInput, integerInput } from "./format";

const customRowsOf = (profile) =>
  (profile ?? []).map((line) => bpsToPercentInput(line.percentBps));

const isEqualShape = (profile) => {
  if (!Array.isArray(profile) || profile.length === 0) return false;
  const expected = equalDistributionProfile(profile.length);
  return profile.every(
    (line, index) =>
      line.offsetMonths === expected[index].offsetMonths &&
      line.percentBps === expected[index].percentBps,
  );
};

const DistributionForm = ({ label, initial, onSave, onClear, onCancel }) => {
  const [startMonth, setStartMonth] = useState(initial?.startMonth ?? "");
  const [shape, setShape] = useState(
    !initial || isEqualShape(initial.profile) ? "equal" : "custom",
  );
  const [installments, setInstallments] = useState(
    String(initial?.profile?.length ?? 6),
  );
  const [rows, setRows] = useState(() =>
    initial ? customRowsOf(initial.profile) : ["100"],
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const built = (() => {
    try {
      const profile =
        shape === "equal"
          ? equalDistributionProfile(integerInput(installments))
          : rows.map((value, index) => ({
              offsetMonths: index,
              percentBps: percentInputToBps(value),
            }));
      return { distribution: validateDistribution({ startMonth, profile }), message: null };
    } catch (caught) {
      return {
        distribution: null,
        message: caught instanceof BudgetPlannerError ? caught.message : "Forma inválida.",
      };
    }
  })();

  const total = rows.reduce((sum, value) => {
    const bps = percentInputToBps(value);
    return Number.isFinite(bps) ? sum + bps : sum;
  }, 0);

  const submit = async () => {
    if (!built.distribution || saving) return;
    setSaving(true);
    setError(null);
    try {
      const result = await onSave(built.distribution);
      if (!result?.ok) setError(result?.message ?? "Não foi possível gravar a distribuição.");
    } catch {
      setError("Não foi possível gravar a distribuição.");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const result = await onClear();
      if (!result?.ok) setError(result?.message ?? "Não foi possível remover a distribuição.");
    } catch {
      setError("Não foi possível remover a distribuição.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="ampli-distribution-form"
      aria-label={`Distribuição de ${label}`}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <label>
        <span>Competência de início</span>
        <input
          type="month"
          aria-label="Competência de início"
          value={startMonth}
          onChange={(event) => setStartMonth(event.target.value)}
        />
      </label>
      <fieldset>
        <legend>Forma da distribuição</legend>
        <label>
          <input
            type="radio"
            name={`shape-${label}`}
            checked={shape === "equal"}
            onChange={() => setShape("equal")}
          />
          <span>Parcelas mensais iguais</span>
        </label>
        <label>
          <input
            type="radio"
            name={`shape-${label}`}
            checked={shape === "custom"}
            onChange={() => setShape("custom")}
          />
          <span>Personalizada</span>
        </label>
      </fieldset>
      {shape === "equal" ? (
        <label>
          <span>Número de parcelas</span>
          <input
            type="number"
            min={1}
            max={MAX_DISTRIBUTION_LINES}
            aria-label="Número de parcelas"
            value={installments}
            onChange={(event) => setInstallments(event.target.value)}
          />
        </label>
      ) : (
        <div>
          <table aria-label="Percentual por mês depois do início">
            <thead>
              <tr>
                <th scope="col">Mês depois do início</th>
                <th scope="col">Percentual</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {rows.map((value, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={index}>
                  <th scope="row">{index}</th>
                  <td>
                    <input
                      type="text"
                      aria-label={`Percentual do mês ${index}`}
                      value={value}
                      onChange={(event) =>
                        setRows((current) =>
                          current.map((item, at) => (at === index ? event.target.value : item)),
                        )
                      }
                    />
                  </td>
                  <td>
                    {rows.length > 1 ? (
                      <button
                        type="button"
                        className="button button-outline small"
                        aria-label={`Remover o mês ${index}`}
                        onClick={() => setRows((current) => current.filter((_item, at) => at !== index))}
                      >
                        Remover
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button
            type="button"
            className="button button-outline small"
            disabled={rows.length >= MAX_DISTRIBUTION_LINES}
            onClick={() => setRows((current) => [...current, "0"])}
          >
            Adicionar mês
          </button>
          <p className="card-label" data-testid="distribution-total">
            {`Soma: ${(total / 100).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`}
          </p>
        </div>
      )}
      {built.message ? (
        <p className="card-label" data-testid="distribution-message">
          {built.message}
        </p>
      ) : null}
      {error ? (
        <p className="error-message" role="alert">
          {error}
        </p>
      ) : null}
      <div className="ampli-form-actions">
        <button type="button" className="button button-ghost small" onClick={onCancel}>
          Cancelar
        </button>
        {initial && onClear ? (
          <button
            type="button"
            className="button button-error small"
            disabled={saving}
            onClick={clear}
          >
            Remover distribuição própria
          </button>
        ) : null}
        <button
          type="submit"
          className="button button-accent small"
          disabled={!built.distribution || saving}
        >
          {saving ? "Gravando…" : "Gravar distribuição"}
        </button>
      </div>
    </form>
  );
};

export default DistributionForm;
